import React, { memo, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Editor } from "react-draft-wysiwyg";
import { EditorState, convertToRaw, ContentState } from "draft-js";
import draftToHtml from "draftjs-to-html";
import htmlToDraft from "html-to-draftjs";

import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import LanguageSwitch from "./shared/language-switch";
import FileUploader from "./shared/file_uploader";

const ContentTranslation = ({ data = [], onChange = () => {}, disabled = false, video = false, images = false }) => {
  const { t, i18n } = useTranslation();
  const [language, setLanguage] = useState(i18n.language);
  const [languageIndex, setLanguageIndex] = useState(0);
  const [editorState, setEditorState] = useState(EditorState.createEmpty());

  useEffect(() => {
    setLanguage(i18n.language);
  }, [i18n.language]);

  useEffect(() => {
    const localeIndex = data.findIndex(item => item.locale === language);

    if (localeIndex >= 0) {
      setLanguageIndex(localeIndex);
      const content = draftToHtml(convertToRaw(editorState.getCurrentContent()));
      // Prevent stack level error
      if (content !== data[localeIndex]?.content) {
        setEditorData(data[localeIndex]?.content);
      }
    } else {
      setLanguageIndex(0);
      setLanguage(data[0]?.locale || language);
      setEditorData(data[0]?.content);
    }
  }, [data, language]);

  useEffect(() => {
    const content = draftToHtml(convertToRaw(editorState.getCurrentContent()));

    if (content !== data[languageIndex]?.content) {
      updateTranslation({ content });
    }
  }, [editorState]);

  const selectLanguage = locale => {
    setLanguage(locale);

    const translations = [...data];

    const localeIndex = translations.findIndex(item => item.locale === locale);
    if (localeIndex >= 0) {
      setLanguageIndex(localeIndex);
      setEditorData(translations[localeIndex]?.content);
    } else {
      translations.push({ title: "", content: "", locale, published: true });
      onChange(translations);
      setLanguageIndex(translations.length - 1);
    }
  };

  // Block example: { field: 'value' }
  const updateTranslation = block => {
    const translations = [...data];
    translations.splice(languageIndex, 1, {
      ...translations[languageIndex],
      locale: language,
      published: true,
      ...block,
    });
    onChange(translations);
  };

  const setEditorData = content => {
    const contentBlock = htmlToDraft(content || "");
    if (contentBlock) {
      const contentState = ContentState.createFromBlockArray(contentBlock.contentBlocks);
      setEditorState(EditorState.createWithContent(contentState));
    }
  };

  return (
    <div className="form-container-content-description">
      <div className="form-container-area-group">
        <div className="form-container-area-group-full-width">
          <div className="heading">
            {t("contentTranslation.title")} <LanguageSwitch onChange={selectLanguage} displayLanguage={language} />
          </div>
          <input
            className="no-border large-input"
            disabled={disabled}
            value={data[languageIndex]?.title || ""}
            onChange={event => updateTranslation({ title: event.target.value })}
          />
        </div>
      </div>

      <div className="heading">{t("contentTranslation.instructions")}</div>
      <div>
        <Editor
          readOnly={disabled}
          editorState={editorState}
          wrapperClassName="editor-wrapper"
          editorClassName="editor-area"
          onEditorStateChange={setEditorState}
          toolbar={{
            options: ["inline", "blockType", "fontSize", "list", "link", ...(images ? ["image"] : [])],
            inline: {
              options: ["bold", "italic", "underline"],
            },
            link: {
              popupClassName: "link-popup",
              inDropdown: false,
              options: ["link"],
            },
            image: {
              uploadCallback: file => {
                return new Promise((resolve, reject) => {
                  const reader = new FileReader();
                  reader.onload = () => {
                    resolve({ data: { link: reader.result } });
                  };
                  reader.onerror = reject;
                  reader.readAsDataURL(file);
                });
              },
              inputAccept: "image/gif,image/jpeg,image/jpg,image/png,image/svg",
              defaultSize: {
                height: "auto",
                width: "auto",
              },
            },
          }}
        />
      </div>
      {video && (
        <>
          <div className="heading">Video</div>
          {data[languageIndex]?.video_url ? (
            <>
              <video className={"video-preview"} src={data[languageIndex]?.video_url} controls />
              {!disabled && (
                <p>
                  <button
                    className={"button button-delete"}
                    onClick={() => updateTranslation({ video_url: null, delete_video_url: true })}
                  >
                    {t("contentTranslation.remove")}
                  </button>
                </p>
              )}
            </>
          ) : disabled ? (
            <span>{t("contentTranslation.no_video")}</span>
          ) : (
            <FileUploader onChange={video_url => updateTranslation({ video_url })} />
          )}
        </>
      )}
    </div>
  );
};

export default memo(ContentTranslation);
